import { ILiquidationState } from "../types";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toAmount } from "../summary";
import {adminRequest, buildQuery} from "../../api/adminApi";

const initialState: ILiquidationState = {
  paymentRequests: null,
  loading: false,
  error: undefined,
  pageInfo: {
    pageNumber: 0,
    pageSize: 0,
    totalItems: 0,
    totalPages: 0,
  },
  summary: {
    totalCount: 0,
    totalAmount: 0,
    pendingCount: 0,
    processingCount: 0,
    completedCount: 0,
  },
};

// GET PAYMENT REQUESTS
export const getPaymentRequests = createAsyncThunk<any, { token: string; searchParam: string; pageNumber: number }>(
  "getPaymentRequests",
  async (data: { token: string; searchParam: string; pageNumber: number }) => {
    return adminRequest(`/group-savings/get-liquidation-requests${buildQuery({
      searchParam: data.searchParam,
      pageSize: 10,
      pageNumber: data.pageNumber,
    })}`, {token: data.token});
  }
);

// APPROVE LIQUIDATION REQUEST (VFD flow)
export const approveLiquidationRequest = createAsyncThunk<any, {
      token: string;
      userId: string;
      entityId: string;
      requestId: string;
    }>(
  "approveLiquidationRequest",
  async (
    data,
    { rejectWithValue }
  ) => {
    try {
      const { token, entityId, userId, requestId } = data;

      return await adminRequest(
        `/group-savings/approve-liquidation-request/${entityId}/${userId}/${requestId}`,
        {token}
      );
    } catch (err) {
      return rejectWithValue("Network error");
    }
  }
);

// APPROVE LIQUIDATION REQUEST MANUAL
export const approveLiquidationRequestManual = createAsyncThunk<any, {
      token: string;
      userId: string;
      savingsId: string;
      requestId: string;
      proofUrl: string;
      accountName: string;
      accountNumber: string;
      bankCode: string;
      bankName: string;
      externalReference?: string;
      narration?: string;
    }>(
  "approveLiquidationRequestManual",
  async (
    data,
    { rejectWithValue }
  ) => {
    try {
      const {
        token,
        userId,
        savingsId,
        requestId,
        proofUrl,
        accountName,
        accountNumber,
        bankCode,
        bankName,
        externalReference,
        narration,
      } = data;

      return await adminRequest("/group-savings/approve-liquidation-request/manual", {
        method: "POST",
        token,
        body: JSON.stringify({
          savingsId,
          userId,
          requestId,
          proofUrl,
          accountName,
          accountNumber,
          bankCode,
          bankName,
          externalReference,
          narration,
        }),
      });
    } catch (err) {
      return rejectWithValue("Network error");
    }
  }
);

const liquidationSlice = createSlice({
  name: "liquidation",
  initialState,
  reducers: {
    reset: (state) => {
      state.paymentRequests = null;
      state.loading = false;
      state.error = undefined;
      state.pageInfo = {
        pageNumber: 0,
        pageSize: 0,
        totalItems: 0,
        totalPages: 0,
      };
      state.summary = {
        totalCount: 0,
        totalAmount: 0,
        pendingCount: 0,
        processingCount: 0,
        completedCount: 0,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // GET REQUESTS
      .addCase(getPaymentRequests.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(getPaymentRequests.fulfilled, (state, action) => {
        if (!action.payload) return;

        if (action.payload.isSuccessfull === false) {
          state.loading = false;
          state.error = action.payload.message;
          return;
        }

        state.loading = false;
        state.error = undefined;
        const requests = action.payload?.returnedObject?.requests || [];
        state.paymentRequests = requests.map(
          (item: any) => ({ ...item, key: item.id })
        );
        state.pageInfo = action.payload?.returnedObject?.pageInfo;
        state.summary.totalCount = state.pageInfo?.totalItems || requests.length;
        state.summary.totalAmount = requests.reduce((total: number, item: any) => {
          return total + toAmount(item.amount);
        }, 0);
        state.summary.pendingCount = requests.filter((item: any) => {
          const status = String(item.liqudationStatus || item.liquidationStatus || item.status || "").toLowerCase();
          return status.includes("pending");
        }).length;
        state.summary.processingCount = requests.filter((item: any) => {
          const status = String(item.liqudationStatus || item.liquidationStatus || item.status || "").toLowerCase();
          return status.includes("processing");
        }).length;
        state.summary.completedCount = requests.filter((item: any) => {
          const status = String(item.liqudationStatus || item.liquidationStatus || item.status || "").toLowerCase();
          return status.includes("complete");
        }).length;
      })
      .addCase(getPaymentRequests.rejected, (state, action) => {
        state.error = action.error.message;
        state.loading = false;
      })

      // APPROVE REQUEST (VFD)
      .addCase(approveLiquidationRequest.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(approveLiquidationRequest.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(approveLiquidationRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // APPROVE REQUEST (MANUAL)
      .addCase(approveLiquidationRequestManual.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(approveLiquidationRequestManual.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(approveLiquidationRequestManual.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { reset } = liquidationSlice.actions;
export default liquidationSlice.reducer;
